import { Typography, Grid, Button, Theme, Hidden } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from '@mui/material/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { useGetSessionQuery, useGetTokenMutation, useGetUserInfoQuery } from '../../../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { QuestionAnswerOutlined } from '@mui/icons-material';
import { isUnavailable } from '../../../../isInSession';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  mobileButton: {
    padding: '0.8em 0',
    margin: 0,
  },
}));

interface DeviceSelectionScreenProps {
  roomName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ roomName, setStep }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [getToken, { isLoading }] = useGetTokenMutation();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting, room } = useVideoContext();
  const disableButtons = isLoading || isAcquiringLocalTracks || isConnecting;
  const { data: user } = useGetUserInfoQuery();
  const { guid } = useParams<{ guid: string }>();
  const { data: session } = useGetSessionQuery(guid);
  const handleCancel = () => {
    setStep(Steps.roomNameStep)
    navigate('/dashboard')
    room?.disconnect()
  }
  const handleStartOrJoin = async () => {
    try {
      const { token } = await getToken(guid).unwrap()
      videoConnect(token);
    } catch (err) {
      console.error(err)
    }
  };

  if (isLoading || isConnecting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} justifyContent="space-around" alignItems="center"
      className={classes.gutterBottom}
    >
      <Grid item
        marginY={2}
        xs={12}
      >
        <Typography
          variant="h5"
        >
          {roomName}
        </Typography>
      </Grid>
      <Grid container justifyContent="center">
        <Grid item md={7} sm={7} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={user?.username || '???'} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
              {!session?.videoRequired && <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />}
              <Button
                className={classes.mobileButton}
                href='https://vimeo.com/577433735'
                startIcon={<QuestionAnswerOutlined color='disabled' />}
                target='_vimeo'
              >FAQ</Button>
            </Hidden>
            <SettingsMenu mobileButtonClass={classes.mobileButton} />
          </div>
        </Grid>
        <Grid item md={5} sm={5} xs={12}>
          <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
            <div>
              <Hidden smDown>
                <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                {!session?.videoRequired && <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />}
                <Button
                  className={classes.deviceButton}
                  href='https://vimeo.com/577433735'
                  startIcon={<QuestionAnswerOutlined color='disabled' />}
                  target='_vimeo'
                >FAQ</Button>
              </Hidden>
            </div>
            <div className={classes.joinButtons}>
              <Button variant="outlined" color="primary"
                onClick={handleCancel}
                style={{
                  marginRight: 8,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                data-cy-join-now
                disabled={disableButtons ||
                  (user?.role === 'member' &&
                    (!session?.inSession || isUnavailable(session, user?.role))
                  )
                }
                fullWidth
                onClick={handleStartOrJoin}
              >
                {
                  session?.inSession
                    ? <>Join</>
                    : (user?.role === 'practitioner' ||
                      (user?.role === 'admin' && !session?.inSession)
                    )
                      ? <>Start</>
                      : <>Join</>
                } Now
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
